// src/utils/backup.js
import { ref, listAll, getDownloadURL, uploadBytes, getMetadata, deleteObject } from 'firebase/storage';
import { collection, getDocs, doc, setDoc, deleteDoc, updateDoc, getDoc, query, where } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { deleteUser } from 'firebase/auth';
import { isEncryptionEnabled, getStoredPassphrase, encryptData, decryptData } from './encryption';

export const createBackup = async (user, force = false) => {
  try {
    const userDocRef = doc(db, `users/${user.uid}`);
    const userDoc = await getDoc(userDocRef);
    const lastModified = userDoc.exists() ? userDoc.data().lastModified : 0;
    const lastBackup = userDoc.exists() ? userDoc.data().lastBackup || 0 : 0;

    if (!force && lastModified <= lastBackup) {
      return null;
    }

    const data = {};
    const collections = ['vehicles', 'events', 'sessions', 'circuits', 'tires'];
    const encryptionEnabled = await isEncryptionEnabled(user, db);
    const passphrase = encryptionEnabled ? getStoredPassphrase() : null;

    for (const col of collections) {
      const querySnapshot = await getDocs(collection(db, `users/${user.uid}/${col}`));
      let docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Keep data in its current format (encrypted or not) for backup

      data[col] = docs;
    }
    const backupData = JSON.stringify(data, null, 2);
    const timestamp = Date.now();
    const fileName = `hot-setup-backup-${user.uid}-${timestamp}.backup`;
    const backupRef = ref(storage, `backups/${user.uid}/${fileName}`);
    await uploadBytes(backupRef, new Blob([backupData], { type: 'application/octet-stream' }));
    // Manage backup count
    const backupListRef = ref(storage, `backups/${user.uid}`);
    const result = await listAll(backupListRef);
    if (result.items.length > 6) {
      const sorted = result.items.sort((a, b) => a.name.localeCompare(b.name));
      for (let i = 0; i < result.items.length - 6; i++) {
        await deleteObject(sorted[i]);
      }
    }
    const url = await getDownloadURL(backupRef);
    // Update lastBackup
    await updateDoc(userDocRef, { lastBackup: timestamp });
    return { url, fileName };
  } catch (error) {
    console.error('Error creating backup:', error);
  }
};

export const restoreBackup = async (user, backupUrl) => {
  try {
    // Download and parse backup
    const response = await fetch(backupUrl);
    const data = await response.json();
    // Clear existing data
    const collections = ['vehicles', 'events', 'sessions', 'circuits', 'tires'];
    for (const col of collections) {
      const querySnapshot = await getDocs(collection(db, `users/${user.uid}/${col}`));
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    }

    const encryptionEnabled = await isEncryptionEnabled(user, db);
    const passphrase = encryptionEnabled ? getStoredPassphrase() : null;

    // Restore data
    for (const col of collections) {
      if (data[col]) {
        for (const item of data[col]) {
          const { id, ...itemData } = item;
          let finalData = itemData;

          // Decrypt if encrypted
          if (itemData.encrypted && passphrase) {
            try {
              finalData = decryptData(itemData.data, passphrase);
            } catch (error) {
              console.error(`Failed to decrypt ${col} item ${id}:`, error);
              continue; // Skip corrupted items
            }
          }

          await setDoc(doc(db, `users/${user.uid}/${col}/${id}`), finalData);
        }
      }
    }
    alert('Restore completed. Please refresh the page.');
  } catch (error) {
    console.error('Error restoring backup:', error);
  }
};

export const fetchBackups = async (user) => {
  try {
    const backupRef = ref(storage, `backups/${user.uid}`);
    const result = await listAll(backupRef);
    const backupList = await Promise.all(result.items.map(async (itemRef) => {
      const metadata = await getMetadata(itemRef);
      return {
        name: itemRef.name,
        url: await getDownloadURL(itemRef),
        timeCreated: metadata.timeCreated
      };
    }));
    return backupList.sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated));
  } catch (error) {
    console.error('Error fetching backups:', error);
    return [];
  }
};

export const restoreFromData = async (user, data) => {
  try {
    // Clear existing data
    const collections = ['vehicles', 'events', 'sessions', 'circuits', 'tires'];
    for (const col of collections) {
      const querySnapshot = await getDocs(collection(db, `users/${user.uid}/${col}`));
      const deletePromises = querySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deletePromises);
    }

    const encryptionEnabled = await isEncryptionEnabled(user, db);
    const passphrase = encryptionEnabled ? getStoredPassphrase() : null;

    // Restore data
    for (const col of collections) {
      if (data[col]) {
        for (const item of data[col]) {
          const { id, ...itemData } = item;
          let finalData = itemData;

          // Decrypt if encrypted
          if (itemData.encrypted && passphrase) {
            try {
              finalData = decryptData(itemData.data, passphrase);
            } catch (error) {
              console.error(`Failed to decrypt ${col} item ${id}:`, error);
              continue; // Skip corrupted items
            }
          }

          // If encryption is currently enabled, re-encrypt the data in the new format
          if (encryptionEnabled && (col === 'sessions' || col === 'tires')) {
            if (!passphrase) {
              console.error(`Cannot restore ${col} with encryption enabled - no passphrase`);
              continue;
            }

            if (col === 'sessions') {
              // For sessions, keep metadata plain, encrypt sensitive data
              const sensitiveData = {
                setup: finalData.setup,
                powerData: finalData.powerData,
                notes: finalData.notes
              };
              finalData = {
                // Plain text metadata
                vehicleId: finalData.vehicleId,
                eventId: finalData.eventId,
                sessionName: finalData.sessionName,
                comment: finalData.comment,
                time: finalData.time,
                timestamp: finalData.timestamp,
                tireIds: finalData.tireIds,
                // Encrypted sensitive data
                encrypted: true,
                data: encryptData(sensitiveData, passphrase)
              };
            } else if (col === 'tires') {
              // For tires, keep metadata plain, encrypt sensitive data
              const sensitiveData = {
                tireSize: finalData.tireSize,
                tireCompound: finalData.tireCompound,
                treadDepth: finalData.treadDepth,
                laps: finalData.laps,
                mileage: finalData.mileage,
                buildDate: finalData.buildDate
              };
              finalData = {
                // Plain text metadata
                tireId: finalData.tireId,
                dateAdded: finalData.dateAdded,
                active: finalData.active,
                // Encrypted sensitive data
                encrypted: true,
                data: encryptData(sensitiveData, passphrase)
              };
            }
          }

          // Add timestamp if missing
          if (!finalData.timestamp) {
            if (col === 'events' && finalData.startDate) {
              finalData.timestamp = new Date(finalData.startDate).getTime();
            } else {
              finalData.timestamp = id;
            }
          }
          // Normalize types
          if (col === 'events' && finalData.vehicleId) {
            finalData.vehicleId = Number(finalData.vehicleId);
          }
          if (col === 'sessions') {
            if (finalData.vehicleId) finalData.vehicleId = Number(finalData.vehicleId);
            if (finalData.eventId) finalData.eventId = String(finalData.eventId);
          }
          await setDoc(doc(db, `users/${user.uid}/${col}/${id}`), finalData);
        }
      }
    }
    alert('Restore completed. Please refresh the page.');
  } catch (error) {
    console.error('Error restoring from data:', error);
  }
};

export const updateLastModified = async (user) => {
  try {
    const userDocRef = doc(db, `users/${user.uid}`);
    await updateDoc(userDocRef, { lastModified: Date.now() });
  } catch (error) {
    // If doc doesn't exist, create it
    try {
      await setDoc(doc(db, `users/${user.uid}`), { lastModified: Date.now() });
    } catch (err) {
      console.error('Error updating lastModified:', err);
    }
  }
};